import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import PageState from "../components/PageState";
import {
  adminLogin,
  createAdminHomeBanner,
  createAdminCourseLesson,
  createAdminCourseModule,
  createAdminProduct,
  deleteAdminHomeBanner,
  deleteAdminCourseLesson,
  deleteAdminCourseModule,
  deleteAdminProduct,
  deleteAdminUser,
  getAdminHomeBanners,
  getAdminCertificates,
  getAdminCourseModules,
  getAdminProducts,
  getAdminUsers,
  getSiteConfig,
  updateSiteConfig,
  updateAdminHomeBanner,
  updateAdminCourseLesson,
  updateAdminCourseModule,
  updateAdminProduct,
  updateAdminUser,
  type AdminAuthResponse,
  type AdminCertificate,
  type AdminCourseLesson,
  type AdminCourseLessonInput,
  type AdminCourseModule,
  type AdminCourseModuleInput,
  type AdminHomeBanner,
  type AdminHomeBannerInput,
  type AdminProduct,
  type AdminProductInput,
  type AdminUser,
} from "../services/api";
import {
  defaultSiteConfig,
  mergeSiteConfig,
  type NotificationItem,
  type SiteConfig,
} from "../siteConfig";

const ADMIN_SESSION_KEY = "blueacademy.admin.session";
const ADMIN_HELP_DISMISSED_KEY = "blueacademy.admin.help.dismissed";
const LIVE_DAY_OPTIONS = [
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
  { value: 0, label: "Dom" },
];

type AdminView =
  | "overview"
  | "homeBanners"
  | "products"
  | "courseCards"
  | "courseModules"
  | "users"
  | "certificates"
  | "notifications"
  | "settings";

type PeopleTab = "leads" | "customers";

type NotificationFormState = {
  title: string;
  text: string;
  url: string;
};

type ProductFormState = {
  title: string;
  description: string;
  banner_url: string;
  is_course: boolean;
  is_mentoria: boolean;
  is_offer: boolean;
  offer_id: string;
  checkout_url: string;
  product_url: string;
  requires_external_signup: boolean;
  external_signup_url: string;
  open_mode: "same" | "new_tab" | "modal";
  checkout_open_mode: "same" | "new_tab" | "modal";
  is_global: boolean;
  linked_offer_ids: string;
  additional_offer_ids: string;
  hide_offer_ids: string;
  is_active: boolean;
  sort_order: string;
};

const defaultProductForm: ProductFormState = {
  title: "",
  description: "",
  banner_url: "",
  is_course: false,
  is_mentoria: false,
  is_offer: true,
  offer_id: "",
  checkout_url: "",
  product_url: "",
  requires_external_signup: false,
  external_signup_url: "",
  open_mode: "same" as const,
  checkout_open_mode: "same" as const,
  is_global: false,
  linked_offer_ids: "",
  additional_offer_ids: "",
  hide_offer_ids: "",
  is_active: true,
  sort_order: "0",
};

const defaultModuleForm = {
  title: "",
  cover_url: "",
  sort_order: "0",
};

const defaultLessonForm = {
  title: "",
  description: "",
  video_url: "",
  thumbnail_url: "",
  materials: "",
  sort_order: "0",
};

const ADMIN_USERS_PAGE_SIZE = 20;

const defaultHomeBannerForm = {
  title: "",
  subtitle: "",
  image_url: "",
  mobile_image_url: "",
  cta_label: "",
  cta_url: "",
  is_active: true,
  sort_order: "0",
};

const defaultUserForm = {
  name: "",
  email: "",
  customer_email: "",
  phone_number: "",
  customer_phone_number: "",
  cpf: "",
  customer_cpf: "",
  status: "",
  offer_ids: "",
  external_signup_completed: "",
  utm_src: "",
  webhook_created_at: "",
};

const defaultNotificationForm: NotificationFormState = {
  title: "",
  text: "",
  url: "",
};

function parseCommaList(value: string) {
  return Array.from(
    new Set(
      value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
}

function stringifyList(values: string[]) {
  return values.join(", ");
}

function normalizeDateTimeLocal(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function parseMaterials(value: string): Array<{ title: string; url: string }> {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [title, url] = line.split("|").map((part) => part.trim());
      if (!title || !url) return null;
      return { title, url };
    })
    .filter((item): item is { title: string; url: string } => Boolean(item));
}

function stringifyMaterials(materials: Array<{ title: string; url: string }>) {
  return materials.map((item) => `${item.title} | ${item.url}`).join("\n");
}

function getAdminEmbedVideoUrl(url: string) {
  if (!url) return "";
  if (url.includes("player.vimeo.com")) return url;

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  const youtubeMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
  );
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  return url;
}

function createLocalPreviewUrl(file?: File | null) {
  if (!file) return "";
  return URL.createObjectURL(file);
}

function getAdminProductKind(product: AdminProduct) {
  if (product.is_course) return "Curso";
  if (product.is_mentoria) return "Mentoria";
  if (product.is_offer) return "Oferta";
  return "Produto";
}

function getAdminViewFromHash(hash: string): AdminView {
  const normalized = hash.replace(/^#\/?/, "").trim().toLowerCase();

  if (normalized === "admin/home-banners") return "homeBanners";
  if (normalized === "admin/products") return "products";
  if (normalized === "admin/courses/cards") return "courseCards";
  if (normalized === "admin/courses/modules") return "courseModules";
  if (normalized === "admin/users") return "users";
  if (normalized === "admin/certificates") return "certificates";
  if (normalized === "admin/notifications") return "notifications";
  if (normalized === "admin/settings") return "settings";
  return "overview";
}

function getAdminHashFromView(view: AdminView) {
  switch (view) {
    case "homeBanners":
      return "/admin/home-banners";
    case "products":
      return "/admin/products";
    case "courseCards":
      return "/admin/courses/cards";
    case "courseModules":
      return "/admin/courses/modules";
    case "users":
      return "/admin/users";
    case "certificates":
      return "/admin/certificates";
    case "notifications":
      return "/admin/notifications";
    case "settings":
      return "/admin/settings";
    default:
      return "/admin";
  }
}

function getDefaultProductFormForView(view: AdminView): ProductFormState {
  if (view === "courseCards") {
    return {
      ...defaultProductForm,
      is_course: true,
      is_offer: false,
      is_global: true,
      open_mode: "same",
      checkout_open_mode: "same",
    };
  }

  return defaultProductForm;
}

const AdminPage: React.FC = () => {
  const [adminSession, setAdminSession] = useState<AdminAuthResponse | null>(null);
  const [activeView, setActiveView] = useState<AdminView>(() =>
    getAdminViewFromHash(window.location.hash),
  );
  const [showAdminHelp, setShowAdminHelp] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showHomeBannerForm, setShowHomeBannerForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [homeBanners, setHomeBanners] = useState<AdminHomeBanner[]>([]);
  const [certificates, setCertificates] = useState<AdminCertificate[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const usersPageSize = ADMIN_USERS_PAGE_SIZE;
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [courseModules, setCourseModules] = useState<AdminCourseModule[]>([]);
  const [selectedCourseProductId, setSelectedCourseProductId] = useState<number | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [editingHomeBannerId, setEditingHomeBannerId] = useState<number | null>(null);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [editingModuleId, setEditingModuleId] = useState<number | null>(null);
  const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
  const [selectedUserDocument, setSelectedUserDocument] = useState("");
  const [peopleTab, setPeopleTab] = useState<PeopleTab>("leads");
  const [homeBannerForm, setHomeBannerForm] = useState(defaultHomeBannerForm);
  const [productForm, setProductForm] = useState(defaultProductForm);
  const [moduleForm, setModuleForm] = useState(defaultModuleForm);
  const [lessonForm, setLessonForm] = useState(defaultLessonForm);
  const [userForm, setUserForm] = useState(defaultUserForm);
  const [siteConfigForm, setSiteConfigForm] = useState<SiteConfig>(defaultSiteConfig);
  const [notificationForm, setNotificationForm] = useState<NotificationFormState>(defaultNotificationForm);
  const [editingNotificationIndex, setEditingNotificationIndex] = useState<number | null>(null);
  const [homeBannerSearch, setHomeBannerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [moduleSearch, setModuleSearch] = useState("");
  const [lessonSearch, setLessonSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [homeBannerLocalPreview, setHomeBannerLocalPreview] = useState("");
  const [productLocalPreview, setProductLocalPreview] = useState("");
  const [moduleLocalPreview, setModuleLocalPreview] = useState("");
  const [lessonThumbnailLocalPreview, setLessonThumbnailLocalPreview] = useState("");
  const [loadingHomeBanners, setLoadingHomeBanners] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const adminToken = adminSession?.token ?? "";

  useEffect(() => {
    const stored = window.localStorage.getItem(ADMIN_SESSION_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as AdminAuthResponse;
      if (parsed?.token && parsed?.admin) setAdminSession(parsed);
    } catch {
      window.localStorage.removeItem(ADMIN_SESSION_KEY);
    }
  }, []);

  useEffect(() => {
    const syncAdminView = () => {
      setActiveView(getAdminViewFromHash(window.location.hash));
    };

    syncAdminView();
    window.addEventListener("hashchange", syncAdminView);
    return () => window.removeEventListener("hashchange", syncAdminView);
  }, []);

  useEffect(() => {
    if (!adminSession) return;
    if (window.localStorage.getItem(ADMIN_HELP_DISMISSED_KEY) === "true") return;
    setShowAdminHelp(true);
  }, [adminSession]);

  useEffect(() => {
    if (!adminToken) return;

    setLoadingSettings(true);
    getSiteConfig(adminToken)
      .then((remote) => {
        const merged = mergeSiteConfig(remote);
        setSiteConfigForm(merged);
      })
      .catch((loadError) => {
        setError(loadError instanceof Error ? loadError.message : "Não foi possível carregar configurações.");
      })
      .finally(() => setLoadingSettings(false));
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) return;

    setLoadingCertificates(true);
    getAdminCertificates(adminToken)
      .then((response) => setCertificates(response.certificates))
      .catch((loadError) => {
        setError(loadError instanceof Error ? loadError.message : "Não foi possível carregar certificados.");
      })
      .finally(() => setLoadingCertificates(false));
  }, [adminToken]);

  useEffect(() => {
    if (editingProductId) return;
    setProductForm(getDefaultProductFormForView(activeView));
  }, [activeView, editingProductId]);

  const courseProducts = useMemo(
    () => products.filter((product) => product.is_course),
    [products],
  );
  const selectedModule = useMemo(
    () => courseModules.find((module) => module.id === selectedModuleId) ?? null,
    [courseModules, selectedModuleId],
  );
  const isHomeBannerFormOpen = showHomeBannerForm || Boolean(editingHomeBannerId);
  const isProductFormOpen = showProductForm || Boolean(editingProductId);
  const isModuleFormOpen = showModuleForm || Boolean(editingModuleId);
  const isLessonFormOpen = showLessonForm || Boolean(editingLessonId);
  const activeProductsCount = useMemo(
    () => products.filter((product) => product.is_active).length,
    [products],
  );
  const activeHomeBannersCount = useMemo(
    () => homeBanners.filter((banner) => banner.is_active).length,
    [homeBanners],
  );
  const totalLessonsCount = useMemo(
    () =>
      courseModules.reduce((total, module) => total + module.lessons.length, 0),
    [courseModules],
  );
  const filteredProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return products;
    return products.filter((product) =>
      [
        product.title,
        product.description,
        product.offer_id,
        product.product_url,
        product.checkout_url,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [productSearch, products]);
  const filteredHomeBanners = useMemo(() => {
    const term = homeBannerSearch.trim().toLowerCase();
    if (!term) return homeBanners;
    return homeBanners.filter((banner) =>
      [
        banner.title,
        banner.subtitle,
        banner.image_url,
        banner.mobile_image_url,
        banner.cta_label,
        banner.cta_url,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [homeBannerSearch, homeBanners]);
  const filteredModules = useMemo(() => {
    const term = moduleSearch.trim().toLowerCase();
    if (!term) return courseModules;
    return courseModules.filter((module) =>
      [module.title, String(module.sort_order)]
        .some((value) => value.toLowerCase().includes(term)),
    );
  }, [courseModules, moduleSearch]);
  const filteredLessons = useMemo(() => {
    const lessons = selectedModule?.lessons ?? [];
    const term = lessonSearch.trim().toLowerCase();
    if (!term) return lessons;
    return lessons.filter((lesson) =>
      [lesson.title, lesson.description, String(lesson.sort_order)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [lessonSearch, selectedModule?.lessons]);
  const selectedUser = useMemo(
    () => users.find((user) => user.document === selectedUserDocument) ?? null,
    [users, selectedUserDocument],
  );

  useEffect(() => {
    if (!selectedUserDocument) return;
    if (users.some((user) => user.document === selectedUserDocument)) return;
    setSelectedUserDocument("");
  }, [selectedUserDocument, users]);

  const filteredProductCards = useMemo(
    () =>
      activeView === "courseCards"
        ? filteredProducts.filter((product) => product.is_course)
        : filteredProducts,
    [activeView, filteredProducts],
  );
  const featuredRemoteProducts = useMemo(
    () =>
      products
        .filter((product) => product.is_active)
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .slice(0, 4),
    [products],
  );

  useEffect(() => {
    if (!adminToken) return;

    setLoadingHomeBanners(true);
    getAdminHomeBanners(adminToken)
      .then((response) => setHomeBanners(response.banners))
      .catch((loadError) => {
        setError(loadError instanceof Error ? loadError.message : "Não foi possível carregar banners.");
      })
      .finally(() => setLoadingHomeBanners(false));
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) return;

    setLoadingProducts(true);
    getAdminProducts(adminToken)
      .then((response) => setProducts(response.products))
      .catch((loadError) => {
        setError(loadError instanceof Error ? loadError.message : "Não foi possível carregar produtos.");
      })
      .finally(() => setLoadingProducts(false));
  }, [adminToken]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedUserSearch(userSearch.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [userSearch]);

  useLayoutEffect(() => {
    setUsersPage(1);
    setSelectedUserDocument("");
  }, [peopleTab, debouncedUserSearch]);

  useEffect(() => {
    if (!adminToken) return;

    setLoadingUsers(true);
    getAdminUsers(adminToken, {
      kind: peopleTab,
      search: debouncedUserSearch,
      page: usersPage,
      pageSize: usersPageSize,
    })
      .then((response) => {
        setUsers(response.users);
        setUsersTotal(response.total);
        setUsersPage(response.page);
        setUsersTotalPages(response.totalPages);
      })
      .catch((loadError) => {
        setError(loadError instanceof Error ? loadError.message : "Não foi possível carregar usuários.");
      })
      .finally(() => setLoadingUsers(false));
  }, [adminToken, debouncedUserSearch, peopleTab, usersPage, usersPageSize]);

  useEffect(() => {
    if (!courseProducts.length) {
      setSelectedCourseProductId(null);
      return;
    }

    setSelectedCourseProductId((current) => {
      if (current && courseProducts.some((product) => product.id === current)) {
        return current;
      }
      return courseProducts[0].id;
    });
  }, [courseProducts]);

  useEffect(() => {
    if (!adminToken || !selectedCourseProductId) {
      setCourseModules([]);
      setSelectedModuleId(null);
      return;
    }

    setLoadingModules(true);
    getAdminCourseModules(adminToken, selectedCourseProductId)
      .then((response) => {
        setCourseModules(response.modules);
        setSelectedModuleId((current) => {
          if (current && response.modules.some((module) => module.id === current)) {
            return current;
          }
          return response.modules[0]?.id ?? null;
        });
      })
      .catch((loadError) => {
        setError(loadError instanceof Error ? loadError.message : "Não foi possível carregar módulos.");
      })
      .finally(() => setLoadingModules(false));
  }, [adminToken, selectedCourseProductId]);

  useEffect(() => {
    if (!selectedUser) {
      setUserForm(defaultUserForm);
      return;
    }

    setUserForm({
      name: selectedUser.name ?? "",
      email: selectedUser.email ?? "",
      customer_email: selectedUser.customer_email ?? "",
      phone_number: selectedUser.phone_number ?? "",
      customer_phone_number: selectedUser.customer_phone_number ?? "",
      cpf: selectedUser.cpf ?? "",
      customer_cpf: selectedUser.customer_cpf ?? "",
      status: selectedUser.status ?? "",
      offer_ids: stringifyList(selectedUser.offer_ids),
      external_signup_completed: stringifyList(selectedUser.external_signup_completed),
      utm_src: selectedUser.utm_src ?? "",
      webhook_created_at: normalizeDateTimeLocal(selectedUser.webhook_created_at),
    });
  }, [selectedUser]);

  useEffect(() => {
    return () => {
      if (homeBannerLocalPreview) URL.revokeObjectURL(homeBannerLocalPreview);
      if (productLocalPreview) URL.revokeObjectURL(productLocalPreview);
      if (moduleLocalPreview) URL.revokeObjectURL(moduleLocalPreview);
      if (lessonThumbnailLocalPreview) URL.revokeObjectURL(lessonThumbnailLocalPreview);
    };
  }, [
    homeBannerLocalPreview,
    lessonThumbnailLocalPreview,
    moduleLocalPreview,
    productLocalPreview,
  ]);

  function persistAdminSession(nextSession: AdminAuthResponse) {
    setAdminSession(nextSession);
    window.localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(nextSession));
    if (getAdminViewFromHash(window.location.hash) === "overview") {
      window.location.hash = getAdminHashFromView("products");
    }
  }

  function clearAdminSession() {
    setAdminSession(null);
    window.localStorage.removeItem(ADMIN_SESSION_KEY);
    window.location.hash = "/admin/login";
  }

  function navigateAdmin(view: AdminView) {
    const nextHash = getAdminHashFromView(view);
    setActiveView(view);
    if (window.location.hash !== `#${nextHash}`) {
      window.location.hash = nextHash;
    }
  }

  async function handleAdminLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoggingIn(true);
    setLoginError("");

    try {
      const response = await adminLogin(loginForm.email.trim(), loginForm.password);
      persistAdminSession(response);
    } catch (authError) {
      setLoginError(
        authError instanceof Error ? authError.message : "Não foi possível autenticar no admin.",
      );
    } finally {
      setLoggingIn(false);
    }
  }

  async function refreshHomeBanners() {
    if (!adminToken) return;
    const response = await getAdminHomeBanners(adminToken);
    setHomeBanners(response.banners);
  }

  async function refreshProducts() {
    if (!adminToken) return;
    const response = await getAdminProducts(adminToken);
    setProducts(response.products);
  }

  async function refreshUsers() {
    if (!adminToken) return;
    const response = await getAdminUsers(adminToken, {
      kind: peopleTab,
      search: debouncedUserSearch,
      page: usersPage,
      pageSize: usersPageSize,
    });
    setUsers(response.users);
    setUsersTotal(response.total);
    setUsersPage(response.page);
    setUsersTotalPages(response.totalPages);
  }

  async function refreshModules() {
    if (!adminToken || !selectedCourseProductId) return;
    const response = await getAdminCourseModules(adminToken, selectedCourseProductId);
    setCourseModules(response.modules);
    setSelectedModuleId((current) => {
      if (current && response.modules.some((module) => module.id === current)) {
        return current;
      }
      return response.modules[0]?.id ?? null;
    });
  }

  function resetMessages() {
    setFeedback("");
    setError("");
  }

  function resetHomeBannerForm() {
    if (homeBannerLocalPreview) URL.revokeObjectURL(homeBannerLocalPreview);
    setHomeBannerLocalPreview("");
    setEditingHomeBannerId(null);
    setHomeBannerForm(defaultHomeBannerForm);
    setShowHomeBannerForm(false);
  }

  function resetProductForm() {
    if (productLocalPreview) URL.revokeObjectURL(productLocalPreview);
    setProductLocalPreview("");
    setEditingProductId(null);
    setProductForm(getDefaultProductFormForView(activeView));
    setShowProductForm(false);
  }

  function resetModuleForm() {
    if (moduleLocalPreview) URL.revokeObjectURL(moduleLocalPreview);
    setModuleLocalPreview("");
    setEditingModuleId(null);
    setModuleForm(defaultModuleForm);
    setShowModuleForm(false);
  }

  function resetLessonForm() {
    if (lessonThumbnailLocalPreview) URL.revokeObjectURL(lessonThumbnailLocalPreview);
    setLessonThumbnailLocalPreview("");
    setEditingLessonId(null);
    setLessonForm(defaultLessonForm);
    setShowLessonForm(false);
  }

  function openNewHomeBannerForm() {
    resetHomeBannerForm();
    setShowHomeBannerForm(true);
  }

  function openNewProductForm() {
    resetProductForm();
    setShowProductForm(true);
  }

  function openNewModuleForm() {
    resetModuleForm();
    setShowModuleForm(true);
  }

  function openNewLessonForm() {
    resetLessonForm();
    setShowLessonForm(true);
  }

  function startEditProduct(product: AdminProduct) {
    if (productLocalPreview) URL.revokeObjectURL(productLocalPreview);
    setProductLocalPreview("");
    setEditingProductId(product.id);
    setShowProductForm(true);
    setProductForm({
      title: product.title,
      description: product.description,
      banner_url: product.banner_url,
      is_course: product.is_course,
      is_mentoria: product.is_mentoria,
      is_offer: product.is_offer,
      offer_id: product.offer_id,
      checkout_url: product.checkout_url ?? "",
      product_url: product.product_url,
      requires_external_signup: product.requires_external_signup,
      external_signup_url: product.external_signup_url ?? "",
      open_mode: product.open_mode,
      checkout_open_mode: product.checkout_open_mode,
      is_global: product.is_global,
      linked_offer_ids: stringifyList(product.linked_offer_ids),
      additional_offer_ids: stringifyList(product.additional_offer_ids),
      hide_offer_ids: stringifyList(product.hide_offer_ids),
      is_active: product.is_active,
      sort_order: String(product.sort_order ?? 0),
    });
  }

  function startEditHomeBanner(banner: AdminHomeBanner) {
    if (homeBannerLocalPreview) URL.revokeObjectURL(homeBannerLocalPreview);
    setHomeBannerLocalPreview("");
    setEditingHomeBannerId(banner.id);
    setShowHomeBannerForm(true);
    setHomeBannerForm({
      title: banner.title,
      subtitle: banner.subtitle,
      image_url: banner.image_url,
      mobile_image_url: banner.mobile_image_url ?? "",
      cta_label: banner.cta_label,
      cta_url: banner.cta_url ?? "",
      is_active: banner.is_active,
      sort_order: String(banner.sort_order ?? 0),
    });
  }

  async function handleSubmitHomeBanner(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!adminToken) return;

    setSaving(true);
    resetMessages();

    const title = homeBannerForm.title.trim();
    const subtitle = homeBannerForm.subtitle.trim();
    const imageUrl = homeBannerForm.image_url.trim();
    const mobileImageUrl = homeBannerForm.mobile_image_url.trim();
    const ctaLabel = homeBannerForm.cta_label.trim();
    const ctaUrl = homeBannerForm.cta_url.trim();

    if (title.length < 2) {
      setError("Informe um titulo com pelo menos 2 caracteres.");
      setSaving(false);
      return;
    }

    if (!imageUrl) {
      setError("Informe a URL da imagem desktop. O preview local sozinho nao salva no backend.");
      setSaving(false);
      return;
    }

    if (!/\.(png|webp|jpe?g)(\?.*)?$/i.test(imageUrl)) {
      setError("A imagem precisa terminar em PNG, JPG, JPEG ou WEBP.");
      setSaving(false);
      return;
    }

    if (mobileImageUrl && !/\.(png|webp|jpe?g)(\?.*)?$/i.test(mobileImageUrl)) {
      setError("A imagem mobile precisa terminar em PNG, JPG, JPEG ou WEBP.");
      setSaving(false);
      return;
    }

    if ((ctaLabel && !ctaUrl) || (!ctaLabel && ctaUrl)) {
      setError("Preencha label e URL do CTA juntos, ou deixe ambos vazios.");
      setSaving(false);
      return;
    }

    const payload: AdminHomeBannerInput = {
      title,
      subtitle: subtitle || null,
      image_url: imageUrl,
      mobile_image_url: mobileImageUrl || null,
      cta_label: ctaLabel || null,
      cta_url: ctaUrl || null,
      is_active: homeBannerForm.is_active,
      sort_order: Number(homeBannerForm.sort_order || 0),
    };

    try {
      if (editingHomeBannerId) {
        await updateAdminHomeBanner(adminToken, editingHomeBannerId, payload);
        setFeedback("Banner da Home atualizado.");
      } else {
        await createAdminHomeBanner(adminToken, payload);
        setFeedback("Banner da Home criado.");
      }
      await refreshHomeBanners();
      resetHomeBannerForm();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível salvar o banner.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteHomeBanner(id: number) {
    if (!adminToken || !window.confirm("Remover este banner da Home?")) return;
    setSaving(true);
    resetMessages();
    try {
      await deleteAdminHomeBanner(adminToken, id);
      await refreshHomeBanners();
      if (editingHomeBannerId === id) resetHomeBannerForm();
      setFeedback("Banner da Home removido.");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Não foi possível remover o banner.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitProduct(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!adminToken) return;

    setSaving(true);
    resetMessages();

    const title = productForm.title.trim();
    const description = productForm.description.trim();
    const bannerUrl = productForm.banner_url.trim();
    const checkoutUrl = productForm.checkout_url.trim();
    const productUrl = productForm.product_url.trim() || checkoutUrl;
    const externalSignupUrl = productForm.external_signup_url.trim();

    if (title.length < 2) {
      setError("Informe um titulo com pelo menos 2 caracteres.");
      setSaving(false);
      return;
    }

    if (description.length < 2) {
      setError("Informe uma descricao com pelo menos 2 caracteres.");
      setSaving(false);
      return;
    }

    if (!bannerUrl) {
      setError("Informe a URL do banner. O preview local sozinho nao salva no backend.");
      setSaving(false);
      return;
    }

    if (!/\.(png|webp)$/i.test(bannerUrl)) {
      setError("O banner precisa terminar em PNG ou WEBP para ser aceito pelo backend.");
      setSaving(false);
      return;
    }

    if (!productUrl) {
      setError("Informe a URL do produto. Se preferir, use a mesma URL do checkout.");
      setSaving(false);
      return;
    }

    if (productForm.is_offer && !checkoutUrl) {
      setError("Produtos marcados como oferta precisam de uma URL de checkout.");
      setSaving(false);
      return;
    }

    if (productForm.requires_external_signup && !externalSignupUrl) {
      setError("Informe a URL de cadastro externo para este fluxo.");
      setSaving(false);
      return;
    }

    const payload: AdminProductInput = {
      title,
      description,
      banner_url: bannerUrl,
      is_course: productForm.is_course,
      is_mentoria: productForm.is_mentoria,
      is_offer: productForm.is_offer,
      offer_id: productForm.offer_id.trim() || undefined,
      checkout_url: checkoutUrl || null,
      product_url: productUrl,
      requires_external_signup: productForm.requires_external_signup,
      external_signup_url: externalSignupUrl || null,
      open_mode: productForm.open_mode,
      checkout_open_mode: productForm.checkout_open_mode,
      is_global: productForm.is_global,
      linked_offer_ids: parseCommaList(productForm.linked_offer_ids),
      additional_offer_ids: parseCommaList(productForm.additional_offer_ids),
      hide_offer_ids: parseCommaList(productForm.hide_offer_ids),
      is_active: productForm.is_active,
      sort_order: Number(productForm.sort_order || 0),
    };

    try {
      if (editingProductId) {
        await updateAdminProduct(adminToken, editingProductId, payload);
        setFeedback("Produto atualizado com sucesso.");
      } else {
        await createAdminProduct(adminToken, payload);
        setFeedback("Produto criado com sucesso.");
      }
      await refreshProducts();
      resetProductForm();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível salvar o produto.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteProduct(id: number) {
    if (!adminToken || !window.confirm("Remover este produto do admin?")) return;
    setSaving(true);
    resetMessages();
    try {
      await deleteAdminProduct(adminToken, id);
      await refreshProducts();
      if (editingProductId === id) resetProductForm();
      setFeedback("Produto removido.");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Não foi possível remover o produto.");
    } finally {
      setSaving(false);
    }
  }

  function startEditModule(module: AdminCourseModule) {
    if (moduleLocalPreview) URL.revokeObjectURL(moduleLocalPreview);
    setModuleLocalPreview("");
    setEditingModuleId(module.id);
    setShowModuleForm(true);
    setModuleForm({
      title: module.title,
      cover_url: module.cover_url,
      sort_order: String(module.sort_order ?? 0),
    });
    setSelectedModuleId(module.id);
  }

  async function handleSubmitModule(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!adminToken || !selectedCourseProductId) return;

    setSaving(true);
    resetMessages();

    const payload: AdminCourseModuleInput = {
      course_product_id: selectedCourseProductId,
      title: moduleForm.title,
      cover_url: moduleForm.cover_url.trim() || null,
      sort_order: Number(moduleForm.sort_order || 0),
    };

    try {
      if (editingModuleId) {
        await updateAdminCourseModule(adminToken, editingModuleId, payload);
        setFeedback("Módulo atualizado.");
      } else {
        await createAdminCourseModule(adminToken, payload);
        setFeedback("Módulo criado.");
      }
      await refreshModules();
      resetModuleForm();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível salvar o módulo.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteModule(id: number) {
    if (!adminToken || !window.confirm("Remover este módulo e suas aulas?")) return;
    setSaving(true);
    resetMessages();
    try {
      await deleteAdminCourseModule(adminToken, id);
      await refreshModules();
      if (editingModuleId === id) resetModuleForm();
      if (selectedModuleId === id) setSelectedModuleId(null);
      setFeedback("Módulo removido.");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Não foi possível remover o módulo.");
    } finally {
      setSaving(false);
    }
  }

  function startEditLesson(lesson: AdminCourseLesson) {
    if (lessonThumbnailLocalPreview) URL.revokeObjectURL(lessonThumbnailLocalPreview);
    setLessonThumbnailLocalPreview("");
    setEditingLessonId(lesson.id);
    setShowLessonForm(true);
    setLessonForm({
      title: lesson.title,
      description: lesson.description,
      video_url: lesson.video_url,
      thumbnail_url: lesson.thumbnail_url,
      materials: stringifyMaterials(lesson.materials),
      sort_order: String(lesson.sort_order ?? 0),
    });
  }

  function handleLocalImagePreview(
    event: React.ChangeEvent<HTMLInputElement>,
    currentPreview: string,
    setPreview: React.Dispatch<React.SetStateAction<string>>,
  ) {
    const file = event.target.files?.[0];
    if (currentPreview) URL.revokeObjectURL(currentPreview);
    setPreview(createLocalPreviewUrl(file));
    event.target.value = "";
  }

  function closeAdminHelp(rememberChoice = false) {
    if (rememberChoice) {
      window.localStorage.setItem(ADMIN_HELP_DISMISSED_KEY, "true");
    }
    setShowAdminHelp(false);
  }

  async function handleSubmitLesson(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!adminToken || !selectedModuleId) return;

    setSaving(true);
    resetMessages();

    const payload: AdminCourseLessonInput = {
      title: lessonForm.title,
      description: lessonForm.description.trim() || null,
      video_url: lessonForm.video_url,
      thumbnail_url: lessonForm.thumbnail_url.trim() || null,
      materials: parseMaterials(lessonForm.materials),
      sort_order: Number(lessonForm.sort_order || 0),
    };

    try {
      if (editingLessonId) {
        await updateAdminCourseLesson(adminToken, editingLessonId, payload);
        setFeedback("Aula atualizada.");
      } else {
        await createAdminCourseLesson(adminToken, selectedModuleId, payload);
        setFeedback("Aula criada.");
      }
      await refreshModules();
      resetLessonForm();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível salvar a aula.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteLesson(id: number) {
    if (!adminToken || !window.confirm("Remover esta aula?")) return;
    setSaving(true);
    resetMessages();
    try {
      await deleteAdminCourseLesson(adminToken, id);
      await refreshModules();
      if (editingLessonId === id) resetLessonForm();
      setFeedback("Aula removida.");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Não foi possível remover a aula.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveUser(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!adminToken || !selectedUser) return;

    setSaving(true);
    resetMessages();
    try {
      await updateAdminUser(adminToken, selectedUser.document, {
        name: userForm.name.trim() || undefined,
        email: userForm.email.trim() || undefined,
        customer_email: userForm.customer_email.trim() || undefined,
        phone_number: userForm.phone_number.trim() || undefined,
        customer_phone_number: userForm.customer_phone_number.trim() || undefined,
        cpf: userForm.cpf.trim() || undefined,
        customer_cpf: userForm.customer_cpf.trim() || undefined,
        status: userForm.status.trim() || undefined,
        offer_ids: parseCommaList(userForm.offer_ids),
        external_signup_completed: parseCommaList(userForm.external_signup_completed),
        utm_src: userForm.utm_src.trim() || undefined,
        webhook_created_at: userForm.webhook_created_at
          ? new Date(userForm.webhook_created_at).toISOString()
          : undefined,
      });
      await refreshUsers();
      setFeedback("Usuário atualizado.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível atualizar o usuário.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteUser(document: string) {
    if (!adminToken || !window.confirm("Remover este usuário?")) return;
    setSaving(true);
    resetMessages();
    try {
      await deleteAdminUser(adminToken, document);
      await refreshUsers();
      if (selectedUserDocument === document) setSelectedUserDocument("");
      setFeedback("Usuário removido.");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Não foi possível remover o usuário.");
    } finally {
      setSaving(false);
    }
  }

  function updateSiteLinks<K extends keyof SiteConfig["links"]>(
    key: K,
    value: SiteConfig["links"][K],
  ) {
    setSiteConfigForm((current) => ({
      ...current,
      links: {
        ...current.links,
        [key]: value,
      },
    }));
  }

  function updateSiteBrand<K extends keyof SiteConfig["brand"]>(
    key: K,
    value: SiteConfig["brand"][K],
  ) {
    setSiteConfigForm((current) => ({
      ...current,
      brand: {
        ...current.brand,
        [key]: value,
      },
    }));
  }

  function updatePopupConfig<
    Section extends "welcomePopup" | "unlockPopup",
    Key extends keyof SiteConfig[Section],
  >(section: Section, key: Key, value: SiteConfig[Section][Key]) {
    setSiteConfigForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value,
      },
    }));
  }

  function updateLiveConfig<Key extends keyof SiteConfig["live"]>(
    key: Key,
    value: SiteConfig["live"][Key],
  ) {
    setSiteConfigForm((current) => ({
      ...current,
      live: {
        ...current.live,
        [key]: value,
      },
    }));
  }

  function toggleLiveDay(day: number) {
    setSiteConfigForm((current) => {
      const currentDays = current.live.days || [];
      const nextDays = currentDays.includes(day)
        ? currentDays.filter((item) => item !== day)
        : [...currentDays, day];

      return {
        ...current,
        live: {
          ...current.live,
          days: nextDays.sort((a, b) => a - b),
        },
      };
    });
  }

  function normalizeSiteConfigForSave(config: SiteConfig): SiteConfig {
    const isTime = (value: string) => /^\d{2}:\d{2}$/.test(value);
    const liveDays = Array.from(
      new Set((config.live.days || []).filter((day) => day >= 0 && day <= 6)),
    );

    return {
      ...config,
      live: {
        ...config.live,
        days: liveDays.length ? liveDays.sort((a, b) => a - b) : defaultSiteConfig.live.days,
        start: isTime(config.live.start) ? config.live.start : defaultSiteConfig.live.start,
        end: isTime(config.live.end) ? config.live.end : defaultSiteConfig.live.end,
      },
    };
  }

  async function handleSaveSettings(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await handleSaveSettingsSection("Configurações atualizadas.");
  }

  async function handleSaveSettingsSection(successMessage: string) {
    if (!adminToken) return;

    setSaving(true);
    resetMessages();
    try {
      const payload = normalizeSiteConfigForSave(siteConfigForm);
      const response = await updateSiteConfig(adminToken, payload);
      const merged = mergeSiteConfig(response.config);
      setSiteConfigForm(merged);
      setFeedback(successMessage);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível salvar as configurações.");
    } finally {
      setSaving(false);
    }
  }

  async function saveSiteConfig(nextConfig: SiteConfig, successMessage: string) {
    if (!adminToken) return;

    setSaving(true);
    resetMessages();
    try {
      const response = await updateSiteConfig(adminToken, normalizeSiteConfigForSave(nextConfig));
      setSiteConfigForm(mergeSiteConfig(response.config));
      setNotificationForm(defaultNotificationForm);
      setEditingNotificationIndex(null);
      setFeedback(successMessage);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível salvar as configurações.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNotification(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const title = notificationForm.title.trim();
    const text = notificationForm.text.trim();
    const url = notificationForm.url.trim();

    if (!title || !text) {
      setError("Informe título e texto da notificação.");
      return;
    }

    const nextNotification: NotificationItem = {
      id:
        editingNotificationIndex !== null
          ? siteConfigForm.notifications[editingNotificationIndex]?.id
          : `notif-${Date.now()}`,
      title,
      text,
      url,
    };
    const notifications = [...siteConfigForm.notifications];

    if (editingNotificationIndex !== null) {
      notifications[editingNotificationIndex] = nextNotification;
    } else {
      notifications.unshift(nextNotification);
    }

    await saveSiteConfig(
      {
        ...siteConfigForm,
        notifications,
      },
      editingNotificationIndex !== null ? "Notificação atualizada." : "Notificação criada.",
    );
  }

  function startEditNotification(index: number) {
    const notification = siteConfigForm.notifications[index];
    if (!notification) return;
    setEditingNotificationIndex(index);
    setNotificationForm({
      title: notification.title,
      text: notification.text,
      url: notification.url || "",
    });
    resetMessages();
  }

  async function handleDeleteNotification(index: number) {
    if (!window.confirm("Remover esta notificação?")) return;
    await saveSiteConfig(
      {
        ...siteConfigForm,
        notifications: siteConfigForm.notifications.filter((_, itemIndex) => itemIndex !== index),
      },
      "Notificação removida.",
    );
  }

  function resetNotificationForm() {
    setNotificationForm(defaultNotificationForm);
    setEditingNotificationIndex(null);
    resetMessages();
  }

  if (!adminSession) {
    return (
      <section className="admin-login-shell">
        <article className="admin-login-panel admin-login-panel-premium glass-panel">
          <div className="admin-login-brand">
            <span className="admin-brand-mark" aria-hidden="true">
              <span>B</span>
            </span>
            <div className="admin-brand-copy" aria-label="Blue Capital">
              <strong>Blue</strong>
              <span>Capital</span>
            </div>
          </div>

          <div className="admin-login-heading">
            <span className="section-eyebrow">Acesso administrativo</span>
            <h1 className="section-title">Painel Blue Capital</h1>
            <p className="section-description">
              Gerencie produtos, banners, cursos e membros em um ambiente seguro.
            </p>
          </div>

          <div className="admin-login-highlights" aria-label="Recursos do painel">
            <span>
              <strong>Produtos</strong>
              <small>Ofertas</small>
            </span>
            <span>
              <strong>Banners</strong>
              <small>Home</small>
            </span>
            <span>
              <strong>Membros</strong>
              <small>Acessos</small>
            </span>
          </div>

          <form className="admin-login-form" onSubmit={handleAdminLogin}>
            <label className="admin-field">
              <span>E-mail</span>
              <input
                className="auth-input"
                type="email"
                autoComplete="username"
                value={loginForm.email}
                onChange={(event) =>
                  setLoginForm((current) => ({ ...current, email: event.target.value }))
                }
              />
            </label>

            <label className="admin-field">
              <span>Senha</span>
              <input
                className="auth-input"
                type="password"
                autoComplete="current-password"
                value={loginForm.password}
                onChange={(event) =>
                  setLoginForm((current) => ({ ...current, password: event.target.value }))
                }
              />
            </label>

            <button className="primary-button" type="submit" disabled={loggingIn}>
              {loggingIn ? "Entrando..." : "Entrar no admin"}
            </button>
          </form>

          {loginError ? <p className="feedback-error">{loginError}</p> : null}
        </article>
      </section>
    );
  }

  const pageTitle =
    activeView === "products"
      ? "Produtos"
      : activeView === "homeBanners"
        ? "Banners da Home"
        : activeView === "courseCards"
          ? "Cards de cursos"
          : activeView === "courseModules"
            ? "Módulos e aulas"
          : activeView === "users"
            ? "Usuários"
            : activeView === "certificates"
              ? "Certificados"
            : activeView === "notifications"
              ? "Notificações"
            : activeView === "settings"
              ? "Configurações"
              : "Visão geral";

  return (
    <section className="admin-premium-shell">
      <header className="admin-topbar glass-panel">
        <div className="admin-topbar-brand">
          <span className="admin-brand-mark admin-brand-mark-small" aria-hidden="true">
            <span>B</span>
          </span>
          <div className="admin-brand-copy admin-brand-copy-small" aria-label="Blue Capital">
            <strong>Blue</strong>
            <span>Capital</span>
            <small>Admin console</small>
          </div>
        </div>

        <div className="admin-topbar-context">
          <span className="section-eyebrow">Área atual</span>
          <strong>{pageTitle}</strong>
        </div>

        <div className="admin-topbar-actions">
          <span className="admin-topbar-user">{adminSession.admin.email}</span>
          <button type="button" className="secondary-button" onClick={() => setShowAdminHelp(true)}>
            Guia rápido
          </button>
          <button type="button" className="secondary-button" onClick={clearAdminSession}>
            Sair
          </button>
        </div>
      </header>

      <aside className="admin-side-rail glass-panel">
        <div className="admin-side-copy">
          <span className="section-eyebrow">Admin</span>
          <h2 className="admin-side-title">Operação central da Blue Capital</h2>
          <p className="section-description">Produtos, trilhas e membros.</p>
        </div>

        <div className="admin-side-stats">
          <article className="admin-side-stat">
            <span>Produtos</span>
            <strong>{products.length}</strong>
            <p>{activeProductsCount} ativos</p>
          </article>
          <article className="admin-side-stat">
            <span>Banners</span>
            <strong>{homeBanners.length}</strong>
            <p>{activeHomeBannersCount} ativos</p>
          </article>
          <article className="admin-side-stat">
            <span>Cursos</span>
            <strong>{courseProducts.length}</strong>
            <p>Trilhas</p>
          </article>
          <article className="admin-side-stat">
            <span>Membros</span>
            <strong>{usersTotal || users.length}</strong>
            <p>Base</p>
          </article>
          <article className="admin-side-stat">
            <span>Aulas</span>
            <strong>{totalLessonsCount}</strong>
            <p>Curso atual</p>
          </article>
        </div>

        <nav className="admin-route-nav" aria-label="Admin routes">
          <button
            type="button"
            className={`admin-route-link ${activeView === "overview" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("overview")}
          >
            <strong>Overview</strong>
            <span>Resumo</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "products" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("products")}
          >
            <strong>Produtos</strong>
            <span>Ofertas</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "homeBanners" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("homeBanners")}
          >
            <strong>Banners</strong>
            <span>Home</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "courseCards" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("courseCards")}
          >
            <strong>Cursos</strong>
            <span>Cards</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "courseModules" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("courseModules")}
          >
            <strong>Conteúdo</strong>
            <span>Módulos e aulas</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "users" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("users")}
          >
            <strong>Usuários</strong>
            <span>Base</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "certificates" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("certificates")}
          >
            <strong>Certificados</strong>
            <span>Emissões</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "notifications" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("notifications")}
          >
            <strong>Notificações</strong>
            <span>Sininho</span>
          </button>
          <button
            type="button"
            className={`admin-route-link ${activeView === "settings" ? "is-active" : ""}`}
            onClick={() => navigateAdmin("settings")}
          >
            <strong>Configurações</strong>
            <span>Links e modal</span>
          </button>
        </nav>

        <div className="admin-side-footer">
          <div className="admin-account-card">
            <span className="section-eyebrow">Sessão ativa</span>
            <strong>{adminSession.admin.email}</strong>
            <p>
            {activeView === "products"
              ? "Gerenciando catálogo e ofertas."
              : activeView === "homeBanners"
                ? "Gerenciando carrossel da Home."
              : activeView === "courseCards"
                ? "Editando apenas os cards de curso."
                  : activeView === "courseModules"
                    ? "Editando trilhas, módulos e aulas."
                    : activeView === "users"
                      ? "Revisando membros e permissões."
                      : activeView === "certificates"
                        ? "Acompanhando certificados emitidos."
                      : activeView === "notifications"
                        ? "Criando avisos para o sininho dos alunos."
                      : activeView === "settings"
                        ? "Ajustando links, corretora e modal de liberação."
                      : "Painel remoto mapeado e pronto para operar."}
            </p>
          </div>
          <span className="admin-side-pill">{pageTitle}</span>
        </div>
      </aside>

      <div className="admin-main-stack">
        {activeView === "overview" ? (
          <>
            <section className="admin-insight-strip">
              <article className="admin-insight-card glass-panel admin-insight-card-primary">
                <span className="section-eyebrow">Painel remoto</span>
                <strong>Estrutura espelhada da VPS</strong>
                <p>
                  Produtos, cards de curso, módulos, aulas e usuários organizados
                  por áreas como no admin da VPS.
                </p>
              </article>

              <article className="admin-insight-card glass-panel">
                <span className="section-eyebrow">Situação atual</span>
                <strong>{products.length} produtos e {usersTotal || users.length} usuários</strong>
                <p>Base real pronta para validação de CRUD e reflexo no front do aluno.</p>
              </article>
            </section>

            <section className="admin-overview-rail">
              {featuredRemoteProducts.map((product) => (
                <article key={product.id} className="admin-overview-card glass-panel">
                  <div className="admin-overview-card-media">
                    {product.banner_url ? (
                      <img
                        src={product.banner_url}
                        alt={product.title}
                        className="admin-product-card-image"
                      />
                    ) : (
                      <span className="admin-product-card-fallback">
                        {product.title.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="admin-overview-card-copy">
                    <span className="section-eyebrow">{getAdminProductKind(product)}</span>
                    <strong>{product.title}</strong>
                    <p>{product.description || "Sem descrição cadastrada."}</p>
                  </div>
                </article>
              ))}
            </section>
          </>
        ) : null}

        <section className="admin-insight-strip">
          <article className="admin-insight-card glass-panel admin-insight-card-primary">
            <span className="section-eyebrow">Área ativa</span>
            <strong>
              {activeView === "products"
                ? "Catálogo e ofertas"
                : activeView === "homeBanners"
                  ? "Carrossel da Home"
                : activeView === "courseCards"
                  ? "Cards de curso"
                  : activeView === "courseModules"
                  ? "Trilhas e aulas"
                  : activeView === "users"
                    ? "Gestão de membros"
                    : activeView === "certificates"
                      ? "Certificados emitidos"
                    : activeView === "notifications"
                      ? "Notificações"
                    : activeView === "settings"
                      ? "Configurações do site"
                    : "Visão geral do admin"}
            </strong>
            <p>
              {activeView === "products"
                ? "Cadastre e organize a vitrine."
                : activeView === "homeBanners"
                  ? "Cadastre banners para substituir o vídeo da Home quando houver itens ativos."
                : activeView === "courseCards"
                  ? "Cadastre somente os cards de curso."
                  : activeView === "courseModules"
                    ? "Monte trilhas, módulos e aulas."
                    : activeView === "users"
                      ? "Edite membros e acessos."
                      : activeView === "certificates"
                        ? "Veja os certificados gerados pelos alunos."
                      : activeView === "notifications"
                        ? "Crie, edite e remova avisos do sininho."
                      : activeView === "settings"
                        ? "Atualize links públicos, corretora e conteúdo do modal."
                      : "Navegue pelas áreas principais do painel."}
            </p>
          </article>

          <article className="admin-insight-card glass-panel">
            <span className="section-eyebrow">Estado atual</span>
            <strong>{feedback ? "Última ação concluída" : error ? "Atenção necessária" : "Operação estável"}</strong>
            <p>{feedback || error || "Painel pronto para operar."}</p>
          </article>
        </section>

        {(feedback || error) ? (
          <section className={`admin-status-banner glass-panel ${error ? "is-error" : "is-success"}`}>
            <span className="section-eyebrow">{error ? "Conexão" : "Atualização"}</span>
            <strong>{error ? "Algo precisa de atenção" : "Tudo certo por aqui"}</strong>
            <p>{error || feedback}</p>
          </section>
        ) : null}

        {activeView === "homeBanners" ? (
          <div className={`admin-grid ${isHomeBannerFormOpen ? "" : "admin-grid-single"}`}>
            {isHomeBannerFormOpen ? (
            <article className="admin-panel glass-panel">
              <div className="admin-panel-heading">
                <div>
                  <span className="section-eyebrow">Home</span>
                  <h2 className="section-title">
                    {editingHomeBannerId ? "Editar banner" : "Novo banner"}
                  </h2>
                </div>
                <button type="button" className="secondary-button" onClick={resetHomeBannerForm}>
                  Cancelar
                </button>
              </div>

              <form className="admin-form" onSubmit={handleSubmitHomeBanner}>
                <section className="admin-form-checklist">
                  <span className="section-eyebrow">Checklist rapido</span>
                  <div className="admin-form-checklist-grid">
                    <span>Titulo preenchido</span>
                    <span>Imagem desktop em PNG, JPG ou WEBP</span>
                    <span>Imagem mobile opcional</span>
                    <span>CTA opcional com label e URL</span>
                  </div>
                </section>

                <section className="admin-form-section">
                  <div className="admin-form-section-head">
                    <span className="section-eyebrow">Conteúdo</span>
                    <p className="section-description">Configure o slide que substitui o vídeo quando estiver ativo.</p>
                  </div>
                  <div className="admin-form-section-grid">
                    <label className="admin-field">
                      <span>Título</span>
                      <input className="auth-input" value={homeBannerForm.title} onChange={(event) => setHomeBannerForm((current) => ({ ...current, title: event.target.value }))} />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Subtítulo</span>
                      <textarea className="auth-input admin-textarea" value={homeBannerForm.subtitle} onChange={(event) => setHomeBannerForm((current) => ({ ...current, subtitle: event.target.value }))} />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Imagem desktop URL</span>
                      <input className="auth-input" value={homeBannerForm.image_url} onChange={(event) => setHomeBannerForm((current) => ({ ...current, image_url: event.target.value }))} />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Imagem mobile URL</span>
                      <input className="auth-input" value={homeBannerForm.mobile_image_url} onChange={(event) => setHomeBannerForm((current) => ({ ...current, mobile_image_url: event.target.value }))} />
                    </label>
                    <label className="admin-field">
                      <span>CTA label</span>
                      <input className="auth-input" value={homeBannerForm.cta_label} onChange={(event) => setHomeBannerForm((current) => ({ ...current, cta_label: event.target.value }))} placeholder="Ex: Conhecer oferta" />
                    </label>
                    <label className="admin-field">
                      <span>CTA URL</span>
                      <input className="auth-input" value={homeBannerForm.cta_url} onChange={(event) => setHomeBannerForm((current) => ({ ...current, cta_url: event.target.value }))} placeholder="https://..." />
                    </label>
                    <label className="admin-field">
                      <span>Sort order</span>
                      <input className="auth-input" type="number" value={homeBannerForm.sort_order} onChange={(event) => setHomeBannerForm((current) => ({ ...current, sort_order: event.target.value }))} />
                    </label>
                  </div>

                  <div className="admin-form-grid">
                    <label className="admin-toggle">
                      <input type="checkbox" checked={homeBannerForm.is_active} onChange={(event) => setHomeBannerForm((current) => ({ ...current, is_active: event.target.checked }))} />
                      <span>Ativo na Home</span>
                    </label>
                  </div>

                  <label className="admin-field admin-field-span-2">
                    <span>Preview local</span>
                    <input
                      className="auth-input"
                      type="file"
                      accept="image/png,image/webp,image/jpeg"
                      onChange={(event) => handleLocalImagePreview(event, homeBannerLocalPreview, setHomeBannerLocalPreview)}
                    />
                    <small className="admin-upload-note">Arquivo local serve apenas para preview. Para salvar no backend, informe a URL da imagem.</small>
                  </label>
                </section>

                {homeBannerLocalPreview || homeBannerForm.image_url.trim() ? (
                  <div className="admin-media-preview">
                    <span className="section-eyebrow">Preview</span>
                    <div className="admin-media-frame">
                      <img src={homeBannerLocalPreview || homeBannerForm.image_url} alt="Preview do banner da Home" className="admin-media-image" />
                    </div>
                  </div>
                ) : null}

                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? "Salvando..." : editingHomeBannerId ? "Atualizar banner" : "Criar banner"}
                </button>
              </form>
            </article>
            ) : null}

            <article className="admin-panel glass-panel">
              <div className="admin-panel-heading">
                <div>
                  <span className="section-eyebrow">Slides</span>
                  <h2 className="section-title">Banners cadastrados</h2>
                </div>
                <button type="button" className="primary-button" onClick={openNewHomeBannerForm}>
                  Novo banner
                </button>
              </div>

              <label className="admin-field admin-search-field">
                <span>Buscar</span>
                <input
                  className="auth-input"
                  value={homeBannerSearch}
                  onChange={(event) => setHomeBannerSearch(event.target.value)}
                  placeholder="Filtrar por titulo, URL ou CTA"
                />
              </label>

              {loadingHomeBanners ? (
                <PageState eyebrow="Admin" title="Carregando banners" description="Lendo carrossel da Home." tone="loading" />
              ) : null}

              <div className="admin-list">
                {filteredHomeBanners.map((banner) => (
                  <article key={banner.id} className="admin-list-card">
                    <div className="admin-product-card-main">
                      <div className="admin-product-card-media">
                        {banner.image_url ? (
                          <img src={banner.image_url} alt={banner.title} className="admin-product-card-image" />
                        ) : (
                          <span className="admin-product-card-fallback">{banner.title.slice(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="admin-product-card-copy">
                        <div className="admin-product-card-topline">
                          <strong>{banner.title}</strong>
                          <div className="admin-product-card-badges">
                            <span className="admin-product-badge">Home</span>
                            <span className={`admin-product-badge ${banner.is_active ? "is-active" : "is-inactive"}`}>
                              {banner.is_active ? "Ativo" : "Inativo"}
                            </span>
                          </div>
                        </div>
                        <p className="admin-product-card-description">
                          {banner.subtitle || "Sem subtítulo cadastrado."}
                        </p>
                        <div className="admin-product-card-meta">
                          <span><strong>Ordem:</strong> {banner.sort_order}</span>
                          <span><strong>Mobile:</strong> {banner.mobile_image_url ? "configurado" : "usa desktop"}</span>
                          <span><strong>CTA:</strong> {banner.cta_label || "sem CTA"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="admin-card-actions">
                      <button type="button" className="secondary-button" onClick={() => startEditHomeBanner(banner)}>Editar</button>
                      <button type="button" className="secondary-button" onClick={() => handleDeleteHomeBanner(banner.id)}>Excluir</button>
                    </div>
                  </article>
                ))}
              </div>

              {!loadingHomeBanners && !filteredHomeBanners.length ? (
                <p className="feedback-note">Nenhum banner da Home encontrado. O vídeo continuará aparecendo como fallback.</p>
              ) : null}
            </article>
          </div>
        ) : null}

        {activeView === "products" || activeView === "courseCards" ? (
          <div className={`admin-grid ${isProductFormOpen ? "" : "admin-grid-single"}`}>
          {isProductFormOpen ? (
          <article className="admin-panel glass-panel">
            <div className="admin-panel-heading">
              <div>
                <span className="section-eyebrow">Cadastro</span>
                <h2 className="section-title">
                  {editingProductId
                    ? activeView === "courseCards"
                      ? "Editar card de curso"
                      : "Editar produto"
                    : activeView === "courseCards"
                      ? "Novo card de curso"
                      : "Novo produto"}
                </h2>
              </div>
              <button type="button" className="secondary-button" onClick={resetProductForm}>Cancelar</button>
            </div>
            <form className="admin-form" onSubmit={handleSubmitProduct}>
              <section className="admin-form-checklist">
                <span className="section-eyebrow">Checklist rapido</span>
                <div className="admin-form-checklist-grid">
                  <span>Titulo e descricao preenchidos</span>
                  <span>Banner URL em PNG ou WEBP</span>
                  <span>URL do produto informada</span>
                  <span>Checkout URL se for oferta</span>
                </div>
              </section>

              <section className="admin-form-section">
                <div className="admin-form-section-head">
                  <span className="section-eyebrow">Identidade</span>
                  <p className="section-description">Base visual e informacoes principais do item.</p>
                </div>
                <div className="admin-form-section-grid">
                  <label className="admin-field"><span>Título</span><input className="auth-input" value={productForm.title} onChange={(event) => setProductForm((current) => ({ ...current, title: event.target.value }))} /></label>
                  <label className="admin-field admin-field-span-2"><span>Descrição</span><textarea className="auth-input admin-textarea" value={productForm.description} onChange={(event) => setProductForm((current) => ({ ...current, description: event.target.value }))} /></label>
                  <label className="admin-field"><span>Banner URL</span><input className="auth-input" value={productForm.banner_url} onChange={(event) => setProductForm((current) => ({ ...current, banner_url: event.target.value }))} /></label>
                  <label className="admin-field"><span>Offer ID</span><input className="auth-input" value={productForm.offer_id} onChange={(event) => setProductForm((current) => ({ ...current, offer_id: event.target.value }))} disabled={productForm.is_offer || (activeView === "courseCards" && !editingProductId)} /></label>
                  <label className="admin-field admin-field-span-2">
                    <span>Preview local do banner</span>
                    <input
                      className="auth-input"
                      type="file"
                      accept="image/png,image/webp,image/jpeg"
                      onChange={(event) => handleLocalImagePreview(event, productLocalPreview, setProductLocalPreview)}
                    />
                    <small className="admin-upload-note">Arquivo local serve apenas para preview. Para salvar no backend, mantenha a URL do banner em PNG ou WEBP.</small>
                  </label>
                </div>
                {productLocalPreview || productForm.banner_url.trim() ? (
                  <div className="admin-media-preview">
                    <span className="section-eyebrow">Preview</span>
                    <div className="admin-media-frame">
                      <img src={productLocalPreview || productForm.banner_url} alt="Preview do banner" className="admin-media-image" />
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="admin-form-section">
                <div className="admin-form-section-head">
                  <span className="section-eyebrow">Links</span>
                  <p className="section-description">Destino principal, checkout e comportamento de abertura.</p>
                </div>
                <div className="admin-form-section-grid">
                  <label className="admin-field admin-field-span-2"><span>URL do produto</span><input className="auth-input" value={productForm.product_url} onChange={(event) => setProductForm((current) => ({ ...current, product_url: event.target.value }))} /></label>
                  <label className="admin-field"><span>Checkout URL</span><input className="auth-input" value={productForm.checkout_url} onChange={(event) => setProductForm((current) => ({ ...current, checkout_url: event.target.value }))} /></label>
                  <label className="admin-field"><span>Abertura</span><select className="auth-input" value={productForm.open_mode} onChange={(event) => setProductForm((current) => ({ ...current, open_mode: event.target.value as "same" | "new_tab" | "modal" }))}><option value="same">Mesma aba</option><option value="new_tab">Nova aba</option><option value="modal">Modal</option></select></label>
                  <label className="admin-field"><span>Checkout</span><select className="auth-input" value={productForm.checkout_open_mode} onChange={(event) => setProductForm((current) => ({ ...current, checkout_open_mode: event.target.value as "same" | "new_tab" | "modal" }))}><option value="same">Mesma aba</option><option value="new_tab">Nova aba</option><option value="modal">Modal</option></select></label>
                  <label className="admin-field"><span>Cadastro externo URL</span><input className="auth-input" value={productForm.external_signup_url} onChange={(event) => setProductForm((current) => ({ ...current, external_signup_url: event.target.value }))} /></label>
                </div>
              </section>

              <section className="admin-form-section">
                <div className="admin-form-section-head">
                  <span className="section-eyebrow">Acesso</span>
                  <p className="section-description">Defina tipo do item, status e regras de liberacao.</p>
                </div>
                <div className="admin-form-grid">
                  <label className="admin-toggle"><input type="checkbox" checked={productForm.is_course} onChange={(event) => setProductForm((current) => ({ ...current, is_course: event.target.checked }))} disabled={activeView === "courseCards"} /><span>É curso</span></label>
                  <label className="admin-toggle"><input type="checkbox" checked={productForm.is_mentoria} onChange={(event) => setProductForm((current) => ({ ...current, is_mentoria: event.target.checked }))} /><span>É mentoria</span></label>
                  <label className="admin-toggle"><input type="checkbox" checked={productForm.is_offer} onChange={(event) => setProductForm((current) => ({ ...current, is_offer: event.target.checked }))} disabled={activeView === "courseCards"} /><span>É oferta</span></label>
                  <label className="admin-toggle"><input type="checkbox" checked={productForm.is_global} onChange={(event) => setProductForm((current) => ({ ...current, is_global: event.target.checked }))} /><span>Global</span></label>
                  <label className="admin-toggle"><input type="checkbox" checked={productForm.requires_external_signup} onChange={(event) => setProductForm((current) => ({ ...current, requires_external_signup: event.target.checked }))} /><span>Cadastro externo</span></label>
                  <label className="admin-toggle"><input type="checkbox" checked={productForm.is_active} onChange={(event) => setProductForm((current) => ({ ...current, is_active: event.target.checked }))} /><span>Ativo</span></label>
                </div>
                <div className="admin-form-section-grid">
                  <label className="admin-field"><span>Sort order</span><input className="auth-input" type="number" value={productForm.sort_order} onChange={(event) => setProductForm((current) => ({ ...current, sort_order: event.target.value }))} /></label>
                </div>
              </section>

              <section className="admin-form-section">
                <div className="admin-form-section-head">
                  <span className="section-eyebrow">Visibilidade</span>
                  <p className="section-description">Controle relacoes entre ofertas, grupos e ocultacao.</p>
                </div>
                <div className="admin-form-section-grid">
                  <label className="admin-field"><span>Linked offer IDs</span><input className="auth-input" value={productForm.linked_offer_ids} onChange={(event) => setProductForm((current) => ({ ...current, linked_offer_ids: event.target.value }))} /></label>
                  <label className="admin-field"><span>Additional offer IDs</span><input className="auth-input" value={productForm.additional_offer_ids} onChange={(event) => setProductForm((current) => ({ ...current, additional_offer_ids: event.target.value }))} /></label>
                  <label className="admin-field"><span>Hide offer IDs</span><input className="auth-input" value={productForm.hide_offer_ids} onChange={(event) => setProductForm((current) => ({ ...current, hide_offer_ids: event.target.value }))} /></label>
                </div>
              </section>
              <button type="submit" className="primary-button" disabled={saving}>{saving ? "Salvando..." : editingProductId ? activeView === "courseCards" ? "Atualizar card" : "Atualizar produto" : activeView === "courseCards" ? "Criar card" : "Criar produto"}</button>
            </form>
          </article>
          ) : null}

          <article className="admin-panel glass-panel">
            <div className="admin-panel-heading">
              <div>
                <span className="section-eyebrow">Itens</span>
                <h2 className="section-title">{activeView === "courseCards" ? "Cards de curso" : "Produtos cadastrados"}</h2>
              </div>
              <button type="button" className="primary-button" onClick={openNewProductForm}>
                {activeView === "courseCards" ? "Novo card" : "Novo produto"}
              </button>
            </div>
            <label className="admin-field admin-search-field">
              <span>Buscar</span>
              <input
                className="auth-input"
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Filtrar por titulo, offer ID ou URL"
              />
            </label>
            {loadingProducts ? <PageState eyebrow="Admin" title="Carregando produtos" description="Lendo o catálogo do admin." tone="loading" /> : null}
            <div className="admin-list">
              {filteredProductCards.map((product) => (
                <article key={product.id} className="admin-list-card">
                  <div className="admin-product-card-main">
                    <div className="admin-product-card-media">
                      {product.banner_url ? (
                        <img
                          src={product.banner_url}
                          alt={product.title}
                          className="admin-product-card-image"
                        />
                      ) : (
                        <span className="admin-product-card-fallback">
                          {product.title.slice(0, 2).toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className="admin-product-card-copy">
                      <div className="admin-product-card-topline">
                        <strong>{product.title}</strong>
                        <div className="admin-product-card-badges">
                          <span className="admin-product-badge">{getAdminProductKind(product)}</span>
                          <span className={`admin-product-badge ${product.is_active ? "is-active" : "is-inactive"}`}>
                            {product.is_active ? "Ativo" : "Inativo"}
                          </span>
                          {product.is_global ? (
                            <span className="admin-product-badge is-global">Global</span>
                          ) : null}
                        </div>
                      </div>

                      <p className="admin-product-card-description">
                        {product.description || "Sem descricao cadastrada."}
                      </p>

                      <div className="admin-product-card-meta">
                        <span>
                          <strong>Offer ID:</strong> {product.offer_id || "sem offer id"}
                        </span>
                        <span>
                          <strong>Ordem:</strong> {product.sort_order}
                        </span>
                        <span>
                          <strong>Abertura:</strong> {product.open_mode}
                        </span>
                        {product.requires_external_signup ? (
                          <span>
                            <strong>Fluxo:</strong> cadastro externo
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="secondary-button" onClick={() => startEditProduct(product)}>Editar</button>
                    <button type="button" className="secondary-button" onClick={() => handleDeleteProduct(product.id)}>Excluir</button>
                  </div>
                </article>
              ))}
            </div>
            {!loadingProducts && !filteredProductCards.length ? (
              <p className="feedback-note">{activeView === "courseCards" ? "Nenhum card de curso encontrado." : "Nenhum produto encontrado."}</p>
            ) : null}
          </article>
          </div>
        ) : null}

        {activeView === "courseModules" ? (
          <div className="admin-grid admin-grid-courses">
          <article className="admin-panel glass-panel">
            <div className="admin-panel-heading">
              <div><span className="section-eyebrow">Curso</span><h2 className="section-title">Conteúdo da trilha</h2></div>
              <button type="button" className="primary-button" onClick={openNewModuleForm}>
                Novo módulo
              </button>
            </div>
            <label className="admin-field">
              <span>Curso</span>
              <select className="auth-input" value={selectedCourseProductId ?? ""} onChange={(event) => setSelectedCourseProductId(Number(event.target.value) || null)}>
                <option value="">Selecione</option>
                {courseProducts.map((product) => <option key={product.id} value={product.id}>{product.title}</option>)}
              </select>
            </label>
            <label className="admin-field admin-search-field">
              <span>Buscar modulo</span>
              <input
                className="auth-input"
                value={moduleSearch}
                onChange={(event) => setModuleSearch(event.target.value)}
                placeholder="Filtrar modulos"
              />
            </label>
            {isModuleFormOpen ? (
            <form className="admin-form" onSubmit={handleSubmitModule}>
              <div className="admin-panel-heading compact">
                <h3>{editingModuleId ? "Editar módulo" : "Novo módulo"}</h3>
                <button type="button" className="secondary-button" onClick={resetModuleForm}>Cancelar</button>
              </div>
              <section className="admin-form-checklist">
                <span className="section-eyebrow">Checklist rapido</span>
                <div className="admin-form-checklist-grid">
                  <span>Curso selecionado</span>
                  <span>Titulo do modulo</span>
                  <span>Capa por URL ou preview</span>
                  <span>Ordem definida</span>
                </div>
              </section>

              <section className="admin-form-section">
                <div className="admin-form-section-head">
                  <span className="section-eyebrow">Módulo</span>
                  <p className="section-description">Estruture a trilha principal do curso selecionado.</p>
                </div>
                <div className="admin-form-section-grid">
                  <label className="admin-field"><span>Título</span><input className="auth-input" value={moduleForm.title} onChange={(event) => setModuleForm((current) => ({ ...current, title: event.target.value }))} /></label>
                  <label className="admin-field"><span>Cover URL</span><input className="auth-input" value={moduleForm.cover_url} onChange={(event) => setModuleForm((current) => ({ ...current, cover_url: event.target.value }))} /></label>
                  <label className="admin-field"><span>Sort order</span><input className="auth-input" type="number" value={moduleForm.sort_order} onChange={(event) => setModuleForm((current) => ({ ...current, sort_order: event.target.value }))} /></label>
                  <label className="admin-field admin-field-span-2">
                    <span>Preview local da capa</span>
                    <input
                      className="auth-input"
                      type="file"
                      accept="image/png,image/webp,image/jpeg"
                      onChange={(event) => handleLocalImagePreview(event, moduleLocalPreview, setModuleLocalPreview)}
                    />
                    <small className="admin-upload-note">O arquivo local aparece no preview, mas o salvamento final ainda acontece pela URL.</small>
                  </label>
                </div>
              </section>
              {moduleLocalPreview || moduleForm.cover_url.trim() ? (
                <div className="admin-media-preview">
                  <span className="section-eyebrow">Preview</span>
                  <div className="admin-media-frame admin-media-frame-cover">
                    <img src={moduleLocalPreview || moduleForm.cover_url} alt="Preview da capa do modulo" className="admin-media-image" />
                  </div>
                </div>
              ) : null}
              <button type="submit" className="primary-button" disabled={saving || !selectedCourseProductId}>{editingModuleId ? "Atualizar módulo" : "Criar módulo"}</button>
            </form>
            ) : null}
            {loadingModules ? <PageState eyebrow="Admin" title="Carregando módulos" description="Buscando módulos e aulas." tone="loading" /> : null}
            <div className="admin-list">
              {filteredModules.map((module) => (
                <article key={module.id} className={`admin-list-card is-clickable ${selectedModuleId === module.id ? "is-selected" : ""}`}>
                  <div className="admin-product-card-main" onClick={() => setSelectedModuleId(module.id)}>
                    <div className="admin-product-card-media">
                      {module.cover_url ? (
                        <img src={module.cover_url} alt={module.title} className="admin-product-card-image" />
                      ) : (
                        <span className="admin-product-card-fallback">{module.title.slice(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="admin-product-card-copy">
                      <div className="admin-product-card-topline">
                        <strong>{module.title}</strong>
                        <div className="admin-product-card-badges">
                          <span className="admin-product-badge">Modulo</span>
                          <span className={`admin-product-badge ${selectedModuleId === module.id ? "is-active" : ""}`}>
                            {selectedModuleId === module.id ? "Selecionado" : "Disponivel"}
                          </span>
                        </div>
                      </div>
                      <div className="admin-product-card-meta">
                        <span><strong>Aulas:</strong> {module.lessons.length}</span>
                        <span><strong>Ordem:</strong> {module.sort_order}</span>
                      </div>
                    </div>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="secondary-button" onClick={() => startEditModule(module)}>Editar</button>
                    <button type="button" className="secondary-button" onClick={() => handleDeleteModule(module.id)}>Excluir</button>
                  </div>
                </article>
              ))}
            </div>
            {!loadingModules && !filteredModules.length ? (
              <p className="feedback-note">Nenhum modulo encontrado.</p>
            ) : null}
          </article>

          <article className="admin-panel glass-panel">
            <div className="admin-panel-heading">
              <div><span className="section-eyebrow">Aulas</span><h2 className="section-title">{selectedModule ? selectedModule.title : "Selecione um módulo"}</h2></div>
              <button type="button" className="primary-button" onClick={openNewLessonForm} disabled={!selectedModuleId}>
                Nova aula
              </button>
            </div>
            <label className="admin-field admin-search-field">
              <span>Buscar aula</span>
              <input
                className="auth-input"
                value={lessonSearch}
                onChange={(event) => setLessonSearch(event.target.value)}
                placeholder="Filtrar aulas"
              />
            </label>
            {isLessonFormOpen ? (
            <form className="admin-form" onSubmit={handleSubmitLesson}>
              <div className="admin-panel-heading compact">
                <h3>{editingLessonId ? "Editar aula" : "Nova aula"}</h3>
                <button type="button" className="secondary-button" onClick={resetLessonForm}>Cancelar</button>
              </div>
              <section className="admin-form-checklist">
                <span className="section-eyebrow">Checklist rapido</span>
                <div className="admin-form-checklist-grid">
                  <span>Modulo selecionado</span>
                  <span>Titulo da aula</span>
                  <span>Video URL valida</span>
                  <span>Thumbnail e materiais opcionais</span>
                </div>
              </section>

              <section className="admin-form-section">
                <div className="admin-form-section-head">
                  <span className="section-eyebrow">Aula</span>
                  <p className="section-description">Monte a experiencia da aula com video, thumbnail e materiais.</p>
                </div>
                <div className="admin-form-section-grid">
                  <label className="admin-field"><span>Título</span><input className="auth-input" value={lessonForm.title} onChange={(event) => setLessonForm((current) => ({ ...current, title: event.target.value }))} /></label>
                  <label className="admin-field admin-field-span-2"><span>Descrição</span><textarea className="auth-input admin-textarea" value={lessonForm.description} onChange={(event) => setLessonForm((current) => ({ ...current, description: event.target.value }))} /></label>
                  <label className="admin-field admin-field-span-2"><span>Vídeo URL</span><input className="auth-input" value={lessonForm.video_url} onChange={(event) => setLessonForm((current) => ({ ...current, video_url: event.target.value }))} /></label>
                  <label className="admin-field"><span>Thumbnail URL</span><input className="auth-input" value={lessonForm.thumbnail_url} onChange={(event) => setLessonForm((current) => ({ ...current, thumbnail_url: event.target.value }))} /></label>
                  <label className="admin-field"><span>Sort order</span><input className="auth-input" type="number" value={lessonForm.sort_order} onChange={(event) => setLessonForm((current) => ({ ...current, sort_order: event.target.value }))} /></label>
                  <label className="admin-field admin-field-span-2">
                    <span>Preview local da thumbnail</span>
                    <input
                      className="auth-input"
                      type="file"
                      accept="image/png,image/webp,image/jpeg"
                      onChange={(event) => handleLocalImagePreview(event, lessonThumbnailLocalPreview, setLessonThumbnailLocalPreview)}
                    />
                    <small className="admin-upload-note">A thumbnail local e so visual. Para gravar a aula, continue informando a URL da thumbnail.</small>
                  </label>
                </div>
              </section>
              {lessonThumbnailLocalPreview || lessonForm.thumbnail_url.trim() || lessonForm.video_url.trim() ? (
                <div className="admin-media-preview admin-media-preview-lesson">
                  <span className="section-eyebrow">Preview</span>
                  <div className="admin-media-preview-grid">
                    {lessonThumbnailLocalPreview || lessonForm.thumbnail_url.trim() ? (
                      <div className="admin-media-frame">
                        <img src={lessonThumbnailLocalPreview || lessonForm.thumbnail_url} alt="Preview da thumbnail" className="admin-media-image" />
                      </div>
                    ) : null}
                    {lessonForm.video_url.trim() ? (
                      <div className="admin-media-frame admin-video-frame">
                        <iframe
                          src={getAdminEmbedVideoUrl(lessonForm.video_url)}
                          title="Preview do video"
                          className="admin-video-embed"
                          allow="autoplay; fullscreen; picture-in-picture"
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <section className="admin-form-section">
                <div className="admin-form-section-head">
                  <span className="section-eyebrow">Materiais</span>
                  <p className="section-description">Adicione apoios em linhas separadas no formato titulo | URL.</p>
                </div>
                <label className="admin-field"><span>Materiais</span><textarea className="auth-input admin-textarea" value={lessonForm.materials} onChange={(event) => setLessonForm((current) => ({ ...current, materials: event.target.value }))} placeholder="Título | https://url-do-material" /></label>
              </section>
              <button type="submit" className="primary-button" disabled={saving || !selectedModuleId}>{editingLessonId ? "Atualizar aula" : "Criar aula"}</button>
            </form>
            ) : null}
            <div className="admin-list">
              {filteredLessons.map((lesson) => (
                <article key={lesson.id} className="admin-list-card">
                  <div className="admin-product-card-main">
                    <div className="admin-product-card-media">
                      {lesson.thumbnail_url ? (
                        <img src={lesson.thumbnail_url} alt={lesson.title} className="admin-product-card-image" />
                      ) : (
                        <span className="admin-product-card-fallback">{lesson.title.slice(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="admin-product-card-copy">
                      <div className="admin-product-card-topline">
                        <strong>{lesson.title}</strong>
                        <div className="admin-product-card-badges">
                          <span className="admin-product-badge">Aula</span>
                          <span className="admin-product-badge">{lesson.materials.length} materiais</span>
                        </div>
                      </div>
                      <p className="admin-product-card-description">
                        {lesson.description || "Sem descricao cadastrada."}
                      </p>
                      <div className="admin-product-card-meta">
                        <span><strong>Ordem:</strong> {lesson.sort_order}</span>
                        <span><strong>Video:</strong> {lesson.video_url ? "configurado" : "nao informado"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="secondary-button" onClick={() => startEditLesson(lesson)}>Editar</button>
                    <button type="button" className="secondary-button" onClick={() => handleDeleteLesson(lesson.id)}>Excluir</button>
                  </div>
                </article>
              ))}
            </div>
            {selectedModule && !filteredLessons.length ? (
              <p className="feedback-note">Nenhuma aula encontrada.</p>
            ) : null}
          </article>
          </div>
        ) : null}

        {activeView === "settings" ? (
          <div className="admin-grid">
            <article className="admin-panel glass-panel">
              <div className="admin-panel-heading">
                <div>
                  <span className="section-eyebrow">Site</span>
                  <h2 className="section-title">Links públicos</h2>
                  <p className="section-description">Esses links aparecem no menu lateral do novo front.</p>
                </div>
              </div>
              {loadingSettings ? (
                <PageState eyebrow="Admin" title="Carregando configurações" description="Lendo as opções salvas." tone="loading" />
              ) : null}
              <form className="admin-form" onSubmit={handleSaveSettings}>
                <section className="admin-form-section">
                  <div className="admin-form-section-head">
                    <span className="section-eyebrow">Marca</span>
                    <p className="section-description">Nome exibido no topo do aplicativo.</p>
                  </div>
                  <div className="admin-form-section-grid">
                    <label className="admin-field">
                      <span>Primeira parte</span>
                      <input className="auth-input" value={siteConfigForm.brand.pre} onChange={(event) => updateSiteBrand("pre", event.target.value)} />
                    </label>
                    <label className="admin-field">
                      <span>Segunda parte</span>
                      <input className="auth-input" value={siteConfigForm.brand.suf} onChange={(event) => updateSiteBrand("suf", event.target.value)} />
                    </label>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="primary-button" disabled={saving} onClick={() => handleSaveSettingsSection("Marca atualizada.")}>
                      {saving ? "Salvando..." : "Salvar marca"}
                    </button>
                  </div>
                </section>

                <section className="admin-form-section">
                  <div className="admin-form-section-head">
                    <span className="section-eyebrow">Comunidade</span>
                    <p className="section-description">Deixe vazio para esconder o item no menu.</p>
                  </div>
                  <div className="admin-form-section-grid">
                    <label className="admin-field admin-field-span-2"><span>Instagram</span><input className="auth-input" value={siteConfigForm.links.instagram} onChange={(event) => updateSiteLinks("instagram", event.target.value)} /></label>
                    <label className="admin-field admin-field-span-2"><span>Telegram</span><input className="auth-input" value={siteConfigForm.links.telegram} onChange={(event) => updateSiteLinks("telegram", event.target.value)} /></label>
                    <label className="admin-field admin-field-span-2"><span>Comunidade WhatsApp</span><input className="auth-input" value={siteConfigForm.links.whatsapp} onChange={(event) => updateSiteLinks("whatsapp", event.target.value)} /></label>
                    <label className="admin-field admin-field-span-2"><span>YouTube</span><input className="auth-input" value={siteConfigForm.links.youtube} onChange={(event) => updateSiteLinks("youtube", event.target.value)} /></label>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="primary-button" disabled={saving} onClick={() => handleSaveSettingsSection("Comunidade atualizada.")}>
                      {saving ? "Salvando..." : "Salvar comunidade"}
                    </button>
                  </div>
                </section>

                <section className="admin-form-section">
                  <div className="admin-form-section-head">
                    <span className="section-eyebrow">Corretora</span>
                    <p className="section-description">Card “Acessar corretora” do menu lateral.</p>
                  </div>
                  <div className="admin-form-section-grid">
                    <label className="admin-field admin-field-span-2"><span>URL da corretora</span><input className="auth-input" value={siteConfigForm.links.broker} onChange={(event) => updateSiteLinks("broker", event.target.value)} /></label>
                    <label className="admin-field"><span>Título</span><input className="auth-input" value={siteConfigForm.links.brokerName} onChange={(event) => updateSiteLinks("brokerName", event.target.value)} /></label>
                    <label className="admin-field"><span>Descrição</span><input className="auth-input" value={siteConfigForm.links.brokerDesc} onChange={(event) => updateSiteLinks("brokerDesc", event.target.value)} /></label>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="primary-button" disabled={saving} onClick={() => handleSaveSettingsSection("Corretora atualizada.")}>
                      {saving ? "Salvando..." : "Salvar corretora"}
                    </button>
                  </div>
                </section>

                <section className="admin-form-section">
                  <div className="admin-form-section-head">
                    <span className="section-eyebrow">Live</span>
                    <p className="section-description">Agenda exibida no painel do aluno como “Próxima live”.</p>
                  </div>
                  <div className="admin-form-section-grid">
                    <label className="admin-toggle">
                      <input
                        type="checkbox"
                        checked={siteConfigForm.live.enabled}
                        onChange={(event) => updateLiveConfig("enabled", event.target.checked)}
                      />
                      <span>Exibir faixa da live</span>
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Título quando estiver ao vivo</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.live.title}
                        onChange={(event) => updateLiveConfig("title", event.target.value)}
                      />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Subtítulo quando estiver ao vivo</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.live.subtitle}
                        onChange={(event) => updateLiveConfig("subtitle", event.target.value)}
                      />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>URL da sala/live</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.live.url}
                        onChange={(event) => updateLiveConfig("url", event.target.value)}
                        placeholder="Vazio usa o link do produto VIP"
                      />
                    </label>
                    <label className="admin-field">
                      <span>Início</span>
                      <input
                        className="auth-input"
                        type="time"
                        value={siteConfigForm.live.start}
                        onChange={(event) => updateLiveConfig("start", event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Fim</span>
                      <input
                        className="auth-input"
                        type="time"
                        value={siteConfigForm.live.end}
                        onChange={(event) => updateLiveConfig("end", event.target.value)}
                      />
                    </label>
                    <div className="admin-field admin-field-span-2">
                      <span>Dias da semana</span>
                      <div className="admin-weekday-list">
                        {LIVE_DAY_OPTIONS.map((day) => (
                          <label className="admin-toggle" key={day.value}>
                            <input
                              type="checkbox"
                              checked={(siteConfigForm.live.days || []).includes(day.value)}
                              onChange={() => toggleLiveDay(day.value)}
                            />
                            <span>{day.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <label className="admin-field admin-field-span-2">
                      <span>Texto fora do horário</span>
                      <textarea
                        className="auth-input admin-textarea"
                        value={siteConfigForm.live.offlineText}
                        onChange={(event) => updateLiveConfig("offlineText", event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="primary-button" disabled={saving} onClick={() => handleSaveSettingsSection("Live atualizada.")}>
                      {saving ? "Salvando..." : "Salvar live"}
                    </button>
                  </div>
                </section>

                <section className="admin-form-section">
                  <div className="admin-form-section-head">
                    <span className="section-eyebrow">Modal</span>
                    <p className="section-description">Conteúdo exibido ao clicar em “Liberar todo o acesso”.</p>
                  </div>
                  <div className="admin-form-section-grid">
                    <label className="admin-field admin-field-span-2">
                      <span>Rótulo pequeno</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.unlockPopup.kicker}
                        onChange={(event) => updatePopupConfig("unlockPopup", "kicker", event.target.value)}
                      />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Título</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.unlockPopup.title}
                        onChange={(event) => updatePopupConfig("unlockPopup", "title", event.target.value)}
                      />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Texto</span>
                      <textarea
                        className="auth-input admin-textarea"
                        value={siteConfigForm.unlockPopup.text}
                        onChange={(event) => updatePopupConfig("unlockPopup", "text", event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Texto do botão principal</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.unlockPopup.ctaLabel}
                        onChange={(event) => updatePopupConfig("unlockPopup", "ctaLabel", event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Link do botão principal</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.unlockPopup.ctaUrl}
                        onChange={(event) => updatePopupConfig("unlockPopup", "ctaUrl", event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Texto do botão secundário</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.unlockPopup.secondaryLabel}
                        onChange={(event) => updatePopupConfig("unlockPopup", "secondaryLabel", event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Imagem opcional</span>
                      <input
                        className="auth-input"
                        value={siteConfigForm.unlockPopup.image || ""}
                        onChange={(event) => updatePopupConfig("unlockPopup", "image", event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="admin-card-actions">
                    <button type="button" className="primary-button" disabled={saving} onClick={() => handleSaveSettingsSection("Modal atualizado.")}>
                      {saving ? "Salvando..." : "Salvar modal"}
                    </button>
                  </div>
                </section>

                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar tudo"}
                </button>
              </form>
            </article>

            <article className="admin-panel glass-panel">
              <div className="admin-panel-heading">
                <div>
                  <span className="section-eyebrow">Preview</span>
                  <h2 className="section-title">O que o aluno verá</h2>
                </div>
              </div>
              <div className="admin-list">
                <article className="admin-list-card">
                  <div className="admin-product-card-copy">
                    <strong>{siteConfigForm.brand.pre}.{siteConfigForm.brand.suf}</strong>
                    <p className="admin-product-card-description">{siteConfigForm.links.brokerName}</p>
                    <div className="admin-product-card-meta">
                      <span>Instagram: {siteConfigForm.links.instagram || "oculto"}</span>
                      <span>Telegram: {siteConfigForm.links.telegram || "oculto"}</span>
                      <span>Comunidade: {siteConfigForm.links.whatsapp || "oculto"}</span>
                      <span>Live: {siteConfigForm.live.enabled ? `${siteConfigForm.live.start} às ${siteConfigForm.live.end}` : "oculta"}</span>
                      <span>Notificações: {siteConfigForm.notifications.length}</span>
                    </div>
                  </div>
                </article>
              </div>
            </article>
          </div>
        ) : null}

        {activeView === "notifications" ? (
          <div className="admin-grid">
            <article className="admin-panel glass-panel">
              <div className="admin-panel-heading">
                <div>
                  <span className="section-eyebrow">Sininho</span>
                  <h2 className="section-title">{editingNotificationIndex !== null ? "Editar notificação" : "Nova notificação"}</h2>
                  <p className="section-description">Esses avisos aparecem no menu do aluno.</p>
                </div>
              </div>
              {loadingSettings ? (
                <PageState eyebrow="Admin" title="Carregando notificações" description="Lendo configurações do site." tone="loading" />
              ) : null}
              <form className="admin-form" onSubmit={handleSaveNotification}>
                <section className="admin-form-section">
                  <div className="admin-form-section-head">
                    <span className="section-eyebrow">Mensagem</span>
                    <p className="section-description">A URL é opcional; quando preenchida, a notificação vira um atalho clicável.</p>
                  </div>
                  <div className="admin-form-section-grid">
                    <label className="admin-field admin-field-span-2">
                      <span>Título</span>
                      <input
                        className="auth-input"
                        value={notificationForm.title}
                        onChange={(event) => setNotificationForm((current) => ({ ...current, title: event.target.value }))}
                        placeholder="Live hoje às 20h"
                      />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>Texto</span>
                      <textarea
                        className="auth-input admin-textarea"
                        value={notificationForm.text}
                        onChange={(event) => setNotificationForm((current) => ({ ...current, text: event.target.value }))}
                        placeholder="Entre pelo menu lateral para acompanhar ao vivo."
                      />
                    </label>
                    <label className="admin-field admin-field-span-2">
                      <span>URL opcional</span>
                      <input
                        className="auth-input"
                        value={notificationForm.url}
                        onChange={(event) => setNotificationForm((current) => ({ ...current, url: event.target.value }))}
                        placeholder="#/curso/cursos ou https://..."
                      />
                    </label>
                  </div>
                </section>
                <div className="admin-card-actions">
                  <button type="submit" className="primary-button" disabled={saving}>
                    {saving ? "Salvando..." : editingNotificationIndex !== null ? "Atualizar notificação" : "Criar notificação"}
                  </button>
                  {editingNotificationIndex !== null ? (
                    <button type="button" className="secondary-button" onClick={resetNotificationForm}>
                      Cancelar edição
                    </button>
                  ) : null}
                </div>
              </form>
            </article>

            <article className="admin-panel glass-panel">
              <div className="admin-panel-heading">
                <div>
                  <span className="section-eyebrow">Ativas</span>
                  <h2 className="section-title">Notificações cadastradas</h2>
                </div>
              </div>
              <div className="admin-list">
                {siteConfigForm.notifications.map((notification, index) => (
                  <article key={notification.id || `${notification.title}-${index}`} className="admin-list-card">
                    <div className="admin-product-card-copy">
                      <div className="admin-product-card-topline">
                        <strong>{notification.title}</strong>
                        <div className="admin-product-card-badges">
                          <span className="admin-product-badge">Sininho</span>
                          {notification.url ? <span className="admin-product-badge">Com link</span> : null}
                        </div>
                      </div>
                      <p className="admin-product-card-description">{notification.text}</p>
                      <div className="admin-product-card-meta">
                        <span>URL: {notification.url || "sem link"}</span>
                      </div>
                    </div>
                    <div className="admin-card-actions">
                      <button type="button" className="secondary-button" onClick={() => startEditNotification(index)}>Editar</button>
                      <button type="button" className="secondary-button" onClick={() => handleDeleteNotification(index)}>Excluir</button>
                    </div>
                  </article>
                ))}
              </div>
              {!siteConfigForm.notifications.length ? (
                <p className="feedback-note">Nenhuma notificação cadastrada.</p>
              ) : null}
            </article>
          </div>
        ) : null}

        {activeView === "certificates" ? (
          <div className="admin-grid admin-grid-single">
            <article className="admin-panel glass-panel">
              <div className="admin-panel-heading">
                <div>
                  <span className="section-eyebrow">Emissões</span>
                  <h2 className="section-title">Certificados gerados</h2>
                  <p className="section-description">Lista dos certificados registrados quando o aluno clica para baixar.</p>
                </div>
              </div>
              {loadingCertificates ? (
                <PageState eyebrow="Admin" title="Carregando certificados" description="Lendo emissões recentes." tone="loading" />
              ) : null}
              <div className="admin-list">
                {certificates.map((certificate) => (
                  <article key={certificate.id} className="admin-list-card">
                    <div className="admin-product-card-copy">
                      <div className="admin-product-card-topline">
                        <strong>{certificate.userName}</strong>
                        <div className="admin-product-card-badges">
                          <span className="admin-product-badge">{certificate.progressPercent}%</span>
                          <span className="admin-product-badge">{certificate.certificateCode}</span>
                        </div>
                      </div>
                      <p className="admin-product-card-description">
                        {certificate.programName}
                      </p>
                      <div className="admin-product-card-meta">
                        <span>E-mail: {certificate.userEmail || "-"}</span>
                        <span>Usuário: {certificate.userId || "-"}</span>
                        <span>Emitido: {new Date(certificate.issuedAt).toLocaleString("pt-BR")}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              {!loadingCertificates && !certificates.length ? (
                <p className="feedback-note">Nenhum certificado emitido ainda.</p>
              ) : null}
            </article>
          </div>
        ) : null}

        {activeView === "users" ? (
          <div className="admin-grid">
          <article className="admin-panel glass-panel">
            <div className="admin-panel-heading">
              <div>
                <span className="section-eyebrow">Membros</span>
                <h2 className="section-title">Lead e Cliente</h2>
              </div>
            </div>
            <div className="admin-people-tabs" role="tablist" aria-label="Alternar entre leads e clientes">
              <button
                type="button"
                className={`admin-people-tab ${peopleTab === "leads" ? "is-active" : ""}`}
                onClick={() => setPeopleTab("leads")}
              >
                Leads
              </button>
              <button
                type="button"
                className={`admin-people-tab ${peopleTab === "customers" ? "is-active" : ""}`}
                onClick={() => setPeopleTab("customers")}
              >
                Clientes
              </button>
            </div>
            <label className="admin-field admin-search-field">
              <span>Buscar</span>
              <input
                className="auth-input"
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                placeholder={peopleTab === "customers" ? "Filtrar clientes" : "Filtrar leads"}
              />
            </label>
            <div className="admin-people-summary">
              <span>
                {peopleTab === "customers" ? "Clientes" : "Leads"} carregados
              </span>
              <strong>
                {users.length} de {usersTotal}
              </strong>
            </div>
            <div className={`admin-people-panel ${peopleTab === "customers" ? "is-customers" : "is-leads"}`}>
              {loadingUsers ? <PageState eyebrow="Admin" title="Carregando usuários" description="Lendo a base principal." tone="loading" /> : null}
              <div className="admin-list">
                {users.map((user) => (
                  <article key={user.document} className={`admin-list-card is-clickable ${selectedUserDocument === user.document ? "is-selected" : ""}`}>
                    <div className="admin-product-card-main" onClick={() => setSelectedUserDocument(user.document)}>
                      <div className="admin-user-avatar">
                        {(user.name || user.document).slice(0, 2).toUpperCase()}
                      </div>
                      <div className="admin-product-card-copy">
                        <div className="admin-product-card-topline">
                          <strong>{user.name || user.document}</strong>
                          <div className="admin-product-card-badges">
                            <span className={`admin-product-badge ${selectedUserDocument === user.document ? "is-active" : ""}`}>
                              {selectedUserDocument === user.document ? "Selecionado" : peopleTab === "customers" ? "Cliente" : "Lead"}
                            </span>
                            {user.status ? (
                              <span className="admin-product-badge">{user.status}</span>
                            ) : null}
                          </div>
                        </div>
                        <p className="admin-product-card-description">
                          {user.email || user.customer_email || "Sem e-mail cadastrado."}
                        </p>
                        <div className="admin-product-card-meta">
                          <span><strong>Telefone:</strong> {user.phone_number || user.customer_phone_number || "-"}</span>
                          <span><strong>CPF:</strong> {user.cpf || user.customer_cpf || "-"}</span>
                          <span><strong>Pedidos:</strong> {user.total_orders}</span>
                        </div>
                      </div>
                    </div>
                    <div className="admin-card-actions">
                      <button type="button" className="secondary-button" onClick={() => setSelectedUserDocument(user.document)}>Editar</button>
                      <button type="button" className="secondary-button" onClick={() => handleDeleteUser(user.document)}>Excluir</button>
                    </div>
                  </article>
                ))}
              </div>
              {!loadingUsers && !users.length ? (
                <p className="feedback-note">Nenhum usuario encontrado.</p>
              ) : null}
              <div className="admin-pagination">
                <span className="admin-pagination-caption">
                  Página {usersPage} de {usersTotalPages}
                </span>
                <div className="admin-pagination-actions">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setUsersPage((current) => Math.max(1, current - 1))}
                    disabled={loadingUsers || usersPage <= 1}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setUsersPage((current) => Math.min(usersTotalPages, current + 1))}
                    disabled={loadingUsers || usersPage >= usersTotalPages}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </div>
          </article>

          <article className="admin-panel glass-panel">
            {selectedUser ? (
              <>
                <div className="admin-panel-heading"><div><span className="section-eyebrow">Editar</span><h2 className="section-title">{selectedUser.name || selectedUser.document}</h2></div></div>
                <form className="admin-form" onSubmit={handleSaveUser}>
                  <section className="admin-form-checklist">
                    <span className="section-eyebrow">Checklist rapido</span>
                    <div className="admin-form-checklist-grid">
                      <span>Documento bloqueado para referencia</span>
                      <span>Dados pessoais atualizados</span>
                      <span>Offers revisados</span>
                      <span>Status e webhook conferidos</span>
                    </div>
                  </section>

                  <section className="admin-form-section">
                    <div className="admin-form-section-head">
                      <span className="section-eyebrow">Perfil</span>
                      <p className="section-description">Informacoes centrais do membro selecionado.</p>
                    </div>
                    <div className="admin-form-section-grid">
                      <label className="admin-field"><span>Documento</span><input className="auth-input" value={selectedUser.document} disabled /></label>
                      <label className="admin-field"><span>Nome</span><input className="auth-input" value={userForm.name} onChange={(event) => setUserForm((current) => ({ ...current, name: event.target.value }))} /></label>
                      <label className="admin-field"><span>Status</span><input className="auth-input" value={userForm.status} onChange={(event) => setUserForm((current) => ({ ...current, status: event.target.value }))} /></label>
                      <label className="admin-field"><span>E-mail</span><input className="auth-input" value={userForm.email} onChange={(event) => setUserForm((current) => ({ ...current, email: event.target.value }))} /></label>
                      <label className="admin-field"><span>E-mail cliente</span><input className="auth-input" value={userForm.customer_email} onChange={(event) => setUserForm((current) => ({ ...current, customer_email: event.target.value }))} /></label>
                      <label className="admin-field"><span>Telefone</span><input className="auth-input" value={userForm.phone_number} onChange={(event) => setUserForm((current) => ({ ...current, phone_number: event.target.value }))} /></label>
                      <label className="admin-field"><span>Telefone cliente</span><input className="auth-input" value={userForm.customer_phone_number} onChange={(event) => setUserForm((current) => ({ ...current, customer_phone_number: event.target.value }))} /></label>
                      <label className="admin-field"><span>CPF</span><input className="auth-input" value={userForm.cpf} onChange={(event) => setUserForm((current) => ({ ...current, cpf: event.target.value }))} /></label>
                      <label className="admin-field"><span>CPF cliente</span><input className="auth-input" value={userForm.customer_cpf} onChange={(event) => setUserForm((current) => ({ ...current, customer_cpf: event.target.value }))} /></label>
                      <label className="admin-field"><span>UTM source</span><input className="auth-input" value={userForm.utm_src} onChange={(event) => setUserForm((current) => ({ ...current, utm_src: event.target.value }))} /></label>
                    </div>
                  </section>

                  <section className="admin-form-section">
                    <div className="admin-form-section-head">
                      <span className="section-eyebrow">Permissões</span>
                      <p className="section-description">Gerencie acessos, ofertas e cadastros externos concluidos.</p>
                    </div>
                    <div className="admin-form-section-grid">
                      <label className="admin-field"><span>É cliente?</span><input className="auth-input" value={String(selectedUser.is_customer)} disabled /></label>
                      <label className="admin-field"><span>Primeira compra</span><input className="auth-input" value={selectedUser.first_purchase_at || ""} disabled /></label>
                      <label className="admin-field"><span>Total de pedidos</span><input className="auth-input" value={String(selectedUser.total_orders)} disabled /></label>
                      <label className="admin-field"><span>Total faturado</span><input className="auth-input" value={String(selectedUser.total_revenue)} disabled /></label>
                      <label className="admin-field"><span>Offer IDs</span><input className="auth-input" value={userForm.offer_ids} onChange={(event) => setUserForm((current) => ({ ...current, offer_ids: event.target.value }))} /></label>
                      <label className="admin-field admin-field-span-2"><span>Cadastros externos concluídos</span><input className="auth-input" value={userForm.external_signup_completed} onChange={(event) => setUserForm((current) => ({ ...current, external_signup_completed: event.target.value }))} /></label>
                    </div>
                  </section>

                  <section className="admin-form-section">
                    <div className="admin-form-section-head">
                      <span className="section-eyebrow">Webhook</span>
                      <p className="section-description">Controle a data de criacao/importacao sincronizada.</p>
                    </div>
                    <div className="admin-form-section-grid">
                      <label className="admin-field"><span>Webhook created at</span><input className="auth-input" type="datetime-local" value={userForm.webhook_created_at} onChange={(event) => setUserForm((current) => ({ ...current, webhook_created_at: event.target.value }))} /></label>
                    </div>
                  </section>
                  <button type="submit" className="primary-button" disabled={saving}>Salvar usuário</button>
                </form>
              </>
            ) : (
              <PageState eyebrow="Admin" title="Selecione um usuário" description="Edite dados e permissões do membro selecionado." />
            )}
          </article>
          </div>
        ) : null}
      </div>

      {showAdminHelp ? (
        <div className="admin-help-overlay" role="dialog" aria-modal="true" aria-labelledby="admin-help-title">
          <article className="admin-help-modal glass-panel">
            <div className="admin-help-heading">
              <span className="section-eyebrow">Tutorial</span>
              <h2 id="admin-help-title" className="section-title">Como usar o painel</h2>
              <p className="section-description">
                Use a navegação lateral para trocar de área. Cada tela tem formulário à esquerda e lista/edição à direita quando necessário.
              </p>
            </div>

            <div className="admin-help-grid">
              <section className="admin-help-step">
                <span>01</span>
                <strong>Overview</strong>
                <p>Veja o resumo do painel e confirme se produtos, banners, cursos e usuários carregaram corretamente.</p>
              </section>
              <section className="admin-help-step">
                <span>02</span>
                <strong>Produtos</strong>
                <p>Cadastre ferramentas, ofertas e acessos. Use busca para encontrar itens e editar sem duplicar cadastro.</p>
              </section>
              <section className="admin-help-step">
                <span>03</span>
                <strong>Banners</strong>
                <p>Controle os banners da Home. Se não houver banner ativo, o vídeo continua aparecendo como fallback.</p>
              </section>
              <section className="admin-help-step">
                <span>04</span>
                <strong>Cursos</strong>
                <p>Cards de curso ficam separados de módulos e aulas. Primeiro escolha o curso, depois organize o conteúdo.</p>
              </section>
              <section className="admin-help-step">
                <span>05</span>
                <strong>Usuários</strong>
                <p>Selecione um membro na lista para revisar dados, offers e permissões antes de salvar alterações.</p>
              </section>
            </div>

            <div className="admin-help-actions">
              <button type="button" className="secondary-button" onClick={() => closeAdminHelp(true)}>
                Não mostrar novamente
              </button>
              <button type="button" className="primary-button" onClick={() => closeAdminHelp()}>
                Entendi
              </button>
            </div>
          </article>
        </div>
      ) : null}
    </section>
  );
};

export default AdminPage;
